import { View, Text, ScrollView, TouchableOpacity, StyleSheet, ActivityIndicator, Linking } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useRetention } from '@/hooks/useRetention';
import { useConversations } from '@/hooks/useConversations';
import { ConversationWithStudent } from '@/lib/types';
import {
  RetentionCategory, CATEGORY_LABEL, CATEGORY_COLOR, CATEGORY_ORDER,
} from '@/lib/retention';

function cleanPhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const d = phone.replace(/\D/g, '');
  if (d.length < 10) return null;
  return d.length <= 11 ? `55${d}` : d;
}

export function DesktopHome() {
  const router = useRouter();
  const { profile } = useAuth();
  const personalId = profile?.id ?? '';
  const { data: ret, isLoading: loadingRet } = useRetention(personalId);
  const { conversations = [], totalUnread = 0 } = useConversations(personalId);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  })();
  const firstName = (profile?.full_name ?? 'Personal').split(' ')[0];

  // Top 5 em risco / abandono (ordenados por dias sem treinar desc)
  const topAttention = ret
    ? [...ret.byCategory.em_risco, ...ret.byCategory.abandono]
        .sort((a, b) => (b.daysSinceLast ?? 0) - (a.daysSinceLast ?? 0))
        .slice(0, 5)
    : [];

  // Conversas não lidas (top 5)
  const unreadConvs = (conversations as ConversationWithStudent[])
    .filter(c => c.unread_personal > 0)
    .slice(0, 5);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>

      {/* Cabeçalho */}
      <View style={styles.header}>
        <View>
          <Text style={styles.greeting}>{greeting}, {firstName}!</Text>
          <Text style={styles.subtitle}>Visão geral da sua operação hoje</Text>
        </View>
        <View style={styles.headerActions}>
          <QuickAction icon="paper-plane-outline" label="Notificar em lote"
            onPress={() => router.push('/(personal)/retencao' as never)} />
        </View>
      </View>

      {loadingRet ? (
        <ActivityIndicator color="#C8102E" style={{ marginTop: 40 }} />
      ) : (
        <>
          {/* Grid 2x2 de widgets */}
          <View style={styles.grid}>

            {/* Widget 1: KPIs */}
            <Widget title="Pulso mensal" onMore={() => router.push('/(personal)/retencao' as never)}>
              <View style={styles.kpiGrid}>
                <Kpi label="Alunos ativos" value={String(ret?.kpis.activeStudents ?? 0)} />
                <Kpi label="Engajamento" value={String(ret?.kpis.engagementRate ?? 0)} unit="%" />
                <Kpi label="Crescimento" value={String(ret?.kpis.growthRate ?? 0)} unit="%" up={(ret?.kpis.growthRate ?? 0) > 0} />
                <Kpi label="Recuperação" value={String(ret?.kpis.recoveryRate ?? 0)} unit="%" up={(ret?.kpis.recoveryRate ?? 0) > 0} />
              </View>
            </Widget>

            {/* Widget 2: Engajamento (barras) */}
            <Widget title="Engajamento da turma" onMore={() => router.push('/(personal)/retencao' as never)}>
              {CATEGORY_ORDER.map((cat: RetentionCategory) => {
                const count = ret?.counts[cat] ?? 0;
                const total = ret?.total ?? 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <View key={cat} style={styles.barRow}>
                    <View style={styles.barHead}>
                      <Text style={styles.barLabel}>{CATEGORY_LABEL[cat]}</Text>
                      <Text style={styles.barValue}>{count} ({pct}%)</Text>
                    </View>
                    <View style={styles.barTrack}>
                      <View style={[styles.barFill, { width: `${pct}%`, backgroundColor: CATEGORY_COLOR[cat] }]} />
                    </View>
                  </View>
                );
              })}
            </Widget>

            {/* Widget 3: Alunos críticos */}
            <Widget title="Precisam de atenção" onMore={() => router.push('/(personal)/retencao' as never)}>
              {topAttention.length === 0 ? (
                <Text style={styles.empty}>Todo mundo treinando em dia 🎯</Text>
              ) : (
                topAttention.map(s => (
                  <View key={s.id} style={styles.attRow}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.attName} numberOfLines={1}>{s.full_name ?? s.email}</Text>
                      <Text style={styles.attMeta}>
                        {s.daysSinceLast == null ? 'nunca treinou' : `${s.daysSinceLast} dia${s.daysSinceLast === 1 ? '' : 's'} sem treinar`}
                        {' · '}
                        <Text style={{ color: CATEGORY_COLOR[s.category] }}>{CATEGORY_LABEL[s.category]}</Text>
                      </Text>
                    </View>
                    <TouchableOpacity
                      onPress={() => {
                        const p = cleanPhone(s.phone);
                        if (!p) return;
                        const first = (s.full_name ?? 'aluno').split(' ')[0];
                        const msg = `Oi ${first}! Vi que faz ${s.daysSinceLast ?? '?'} dias sem treinar. Tá tudo bem?`;
                        Linking.openURL(`https://wa.me/${p}?text=${encodeURIComponent(msg)}`).catch(() => {});
                      }}
                      style={styles.zapBtn}
                      activeOpacity={0.7}
                    >
                      <Ionicons name="logo-whatsapp" size={14} color="#fff" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => router.push(`/(personal)/students/${s.id}` as never)}
                      style={styles.viewBtn}
                      activeOpacity={0.7}
                    >
                      <Ionicons name="chevron-forward" size={16} color="#888" />
                    </TouchableOpacity>
                  </View>
                ))
              )}
            </Widget>

            {/* Widget 4: Chat */}
            <Widget
              title={`Mensagens${totalUnread > 0 ? ` (${totalUnread} não lida${totalUnread > 1 ? 's' : ''})` : ''}`}
              onMore={() => router.push('/(personal)/chat/' as never)}
            >
              {unreadConvs.length === 0 ? (
                <Text style={styles.empty}>Inbox em dia, sem mensagens novas.</Text>
              ) : (
                unreadConvs.map(c => (
                  <TouchableOpacity
                    key={c.id}
                    style={styles.chatRow}
                    onPress={() => router.push(`/(personal)/chat/${c.id}` as never)}
                    activeOpacity={0.7}
                  >
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.chatName} numberOfLines={1}>
                        {c.student?.full_name ?? 'Aluno'}
                      </Text>
                      <Text style={styles.chatLast} numberOfLines={1}>
                        {c.last_message_at
                          ? `Atualizado em ${new Date(c.last_message_at).toLocaleDateString('pt-BR')}`
                          : 'Aguardando primeira mensagem'}
                      </Text>
                    </View>
                    <View style={styles.chatBadge}>
                      <Text style={styles.chatBadgeText}>{c.unread_personal}</Text>
                    </View>
                  </TouchableOpacity>
                ))
              )}
            </Widget>
          </View>
        </>
      )}
    </ScrollView>
  );
}

// ─── sub-componentes ──────────────────────────────────────────────────────

function Widget({ title, children, onMore }: { title: string; children: React.ReactNode; onMore?: () => void }) {
  return (
    <View style={styles.widget}>
      <View style={styles.widgetHead}>
        <Text style={styles.widgetTitle}>{title}</Text>
        {onMore && (
          <TouchableOpacity onPress={onMore} activeOpacity={0.7}>
            <Text style={styles.widgetMore}>Ver mais →</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={{ flex: 1 }}>{children}</View>
    </View>
  );
}

function Kpi({ label, value, unit, up }: { label: string; value: string; unit?: string; up?: boolean }) {
  return (
    <View style={styles.kpiBox}>
      <Text style={styles.kpiLabel}>{label}</Text>
      <Text style={[styles.kpiValue, up && styles.kpiValueUp]}>
        {value}{unit && <Text style={styles.kpiUnit}>{unit}</Text>}
      </Text>
    </View>
  );
}

function QuickAction({ icon, label, onPress }: { icon: keyof typeof Ionicons.glyphMap; label: string; onPress: () => void }) {
  return (
    <TouchableOpacity style={styles.quickAction} onPress={onPress} activeOpacity={0.7}>
      <Ionicons name={icon} size={14} color="#fff" />
      <Text style={styles.quickActionText}>{label}</Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 24, paddingBottom: 48 },

  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 24,
  },
  greeting: { fontSize: 22, fontWeight: '800', color: '#1A1A1A' },
  subtitle: { fontSize: 13, color: '#888', marginTop: 4 },
  headerActions: { flexDirection: 'row', gap: 8 },

  quickAction: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#C8102E', paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
  },
  quickActionText: { color: '#fff', fontSize: 13, fontWeight: '700' },

  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  widget: {
    flexBasis: '48%', flexGrow: 1, minWidth: 320, minHeight: 260,
    backgroundColor: '#fff', borderRadius: 12, padding: 18,
    borderWidth: 1, borderColor: '#EEE',
  },
  widgetHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  widgetTitle: { fontSize: 14, fontWeight: '700', color: '#1A1A1A' },
  widgetMore: { color: '#C8102E', fontSize: 12, fontWeight: '600' },

  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  kpiBox: {
    flexBasis: '47%', flexGrow: 1,
    backgroundColor: '#F9F9F9', borderRadius: 10, padding: 12,
  },
  kpiLabel: { fontSize: 10, color: '#888', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: '800', color: '#1A1A1A' },
  kpiValueUp: { color: '#2E7D32' },
  kpiUnit: { fontSize: 14, fontWeight: '500', color: '#888' },

  barRow: { marginBottom: 10 },
  barHead: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  barLabel: { fontSize: 12, color: '#1A1A1A', fontWeight: '600' },
  barValue: { fontSize: 11, color: '#555' },
  barTrack: { height: 6, backgroundColor: '#EEE', borderRadius: 3, overflow: 'hidden' },
  barFill: { height: 6, borderRadius: 3 },

  attRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F2F2F2',
  },
  attName: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
  attMeta: { fontSize: 11, color: '#888', marginTop: 2 },
  zapBtn: {
    backgroundColor: '#25D366', width: 28, height: 28, borderRadius: 14,
    justifyContent: 'center', alignItems: 'center',
  },
  viewBtn: { padding: 4 },

  chatRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F2F2F2',
  },
  chatName: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
  chatLast: { fontSize: 11, color: '#888', marginTop: 2 },
  chatBadge: {
    backgroundColor: '#C8102E', width: 22, height: 22, borderRadius: 11,
    justifyContent: 'center', alignItems: 'center',
  },
  chatBadgeText: { color: '#fff', fontSize: 11, fontWeight: '700' },

  empty: { fontSize: 13, color: '#888', textAlign: 'center', paddingVertical: 30 },
});
